import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// no lazy loading for auth pages to avoid flickering
const AuthLayout = React.lazy(() => import('@app/components/layouts/AuthLayout/AuthLayout'));
import LoginPage from '@app/pages/LoginPage';
import SignUpPage from '@app/pages/SignUpPage';
import ForgotPasswordPage from '@app/pages/ForgotPasswordPage';
import SecurityCodePage from '@app/pages/SecurityCodePage';
import NewPasswordPage from '@app/pages/NewPasswordPage';
import LockPage from '@app/pages/LockPage';

import MainLayout from '@app/components/layouts/main/MainLayout/MainLayout';
import ProfileLayout from '@app/components/profile/ProfileLayout';
import RequireAuth from '@app/components/router/RequireAuth';
import { withLoading } from '@app/hocs/withLoading.hoc';
import NftDashboardPage from '@app/pages/DashboardPages/NftDashboardPage';

import OTPCodePage from '@app/pages/OTPCodePage';


import ManageUsers from '@app/pages/ManageUsers';



import ManageEmployees from '@app/pages/ManageEmployees';


import CategoriesPage from '@app/pages/CategoriesPages/ManageCategories';
import AddCategory from '../manage-categories/add-category';
import EditCategory from '../manage-categories/edit-category';

import PushNotificationsList from '../manage-push-notifications/list';
import AddPushNotification from '../manage-push-notifications/add-push-notification';
import EditPushNotification from '../manage-push-notifications/edit-push-notification';


import { readUser } from '@app/services/localStorage.service';

import ManageOrder from '@app/pages/OrdersPages/ManageOrder';
import EditOrder from '@app/pages/OrdersPages/EditOrder';


import ThankYouPage from '@app/pages/ThankYouPage/ThankYouPage';



import ProductsPage from '@app/pages/ProductsPages/ManageProducts';
import AddProduct from '../manage-products/add-products';
import EditProduct from '../manage-products/edit-products';
import ManageSubscription from '@app/pages/SubscriptionsPages/ManageSubscription';
import EditSubscription from '@app/pages/SubscriptionsPages/EditSubscription';
import ManageDeliveries from '@app/pages/DeliveryPages/ManageDeliveries';
import EditDelivery from '@app/pages/DeliveryPages/EditDelivery';
import ManageRider from '@app/pages/RiderPages/ManageRiders';
import EditRider from '@app/pages/RiderPages/EditRider';
import ManageClient from '@app/pages/ClientPages/ManageClients';
import EditClient from '@app/pages/ClientPages/EditClient';
import ManageTransactions from '@app/pages/TransactionPages/ManageTransactions';
import EditTransactions from '@app/pages/TransactionPages/EditTransactions';
import ManagePaymentLogs from '@app/pages/PaymentLogsPages/ManagePaymentLogs';
import UsersPage from '@app/pages/UsersPages/ManageUsers';
import AddUser from '../manage-users/add-user';
import EditUser from '../manage-users/edit-user';

import ManageAreasPage from '@app/pages/ManageAreasPage/ManageArea';
import VariantPage from '@app/pages/VariantPages/ManageCategories';
import AddVariant from '../manage-variant/add-variant';
import EditVariant from '../manage-variant/edit-variant';
import ManageInvoices from '@app/pages/InvoicePages/ManageInvoices';
import { BasicTableClientAddresses } from '../manage-clients/client-addresses/BasicTable/BasicTable';
import { ManageRecharges } from '../manage-rechagre';
import ManageRefund from '../manage-refunds';
import ManageDeliverySlots from '@app/pages/DeliverySlotsPages/ManageDeliverySlots';
import ManageFirstRechargeCoupons from '@app/pages/CouponsPages/ManageFirstRechargeCoupons';
import ManageMetaAds from '@app/pages/MetaAdsPages/ManageMetaAds';
import ManageSliders from '@app/pages/SlidersPages/ManageSliders';

const NewsFeedPage = React.lazy(() => import('@app/pages/NewsFeedPage'));
const DataTablesPage = React.lazy(() => import('@app/pages/DataTablesPage'));
const ChartsPage = React.lazy(() => import('@app/pages/ChartsPage'));
const ServerErrorPage = React.lazy(() => import('@app/pages/ServerErrorPage'));
const Error404Page = React.lazy(() => import('@app/pages/Error404Page'));
const AdvancedFormsPage = React.lazy(() => import('@app/pages/AdvancedFormsPage'));
const PersonalInfoPage = React.lazy(() => import('@app/pages/PersonalInfoPage'));
const SecuritySettingsPage = React.lazy(() => import('@app/pages/SecuritySettingsPage'));
const MiscellaneousSettingsPage = React.lazy(() => import('@app/components/MiscellaneousSettings/MiscellaneousSettings'));
const NotificationsPage = React.lazy(() => import('@app/pages/NotificationsPage'));
const PaymentsPage = React.lazy(() => import('@app/pages/PaymentsPage'));
const ButtonsPage = React.lazy(() => import('@app/pages/uiComponentsPages/ButtonsPage'));
const SpinnersPage = React.lazy(() => import('@app/pages/uiComponentsPages/SpinnersPage'));
const AvatarsPage = React.lazy(() => import('@app/pages/uiComponentsPages/dataDisplay/AvatarsPage'));
const BadgesPage = React.lazy(() => import('@app/pages/uiComponentsPages/dataDisplay/BadgesPage'));
const CollapsePage = React.lazy(() => import('@app/pages/uiComponentsPages/dataDisplay/CollapsePage'));
const PaginationPage = React.lazy(() => import('@app/pages/uiComponentsPages/dataDisplay/PaginationPage'));
const ModalsPage = React.lazy(() => import('@app/pages/uiComponentsPages/modals/ModalsPage'));
const PopoversPage = React.lazy(() => import('@app/pages/uiComponentsPages/modals/PopoversPage'));
const PopconfirmsPage = React.lazy(() => import('@app/pages/uiComponentsPages/modals/PopconfirmsPage'));
const ProgressPage = React.lazy(() => import('@app/pages/uiComponentsPages/feedback/ProgressPage'));
const ResultsPage = React.lazy(() => import('@app/pages/uiComponentsPages/feedback/ResultsPage'));
const AlertsPage = React.lazy(() => import('@app/pages/uiComponentsPages/feedback/AlertsPage'));
const SkeletonsPage = React.lazy(() => import('@app/pages/uiComponentsPages/feedback/SkeletonsPage'));
const InputsPage = React.lazy(() => import('@app/pages/uiComponentsPages/forms/InputsPage'));
const CheckboxesPage = React.lazy(() => import('@app/pages/uiComponentsPages/forms/CheckboxesPage'));
const RadiosPage = React.lazy(() => import('@app/pages/uiComponentsPages/forms/RadiosPage'));
const SelectsPage = React.lazy(() => import('@app/pages/uiComponentsPages/forms/SelectsPage'));
const SwitchesPage = React.lazy(() => import('@app/pages/uiComponentsPages/forms/SwitchesPage'));
const UploadsPage = React.lazy(() => import('@app/pages/uiComponentsPages/forms/UploadsPage'));
const RatesPage = React.lazy(() => import('@app/pages/uiComponentsPages/forms/RatesPage'));
const AutoCompletesPage = React.lazy(() => import('@app/pages/uiComponentsPages/forms/AutoCompletesPage'));
const StepsPage = React.lazy(() => import('@app/pages/uiComponentsPages/forms/StepsPage'));
const DateTimePickersPage = React.lazy(() => import('@app/pages/uiComponentsPages/forms/DateTimePickersPage'));
const DropdownsPage = React.lazy(() => import('@app/pages/uiComponentsPages/DropdownsPage'));
const BreadcrumbsPage = React.lazy(() => import('@app/pages/uiComponentsPages/navigation/BreadcrumbsPage'));
const TabsPage = React.lazy(() => import('@app/pages/uiComponentsPages/navigation/TabsPage'));
const NotificationsUIPage = React.lazy(() => import('@app/pages/uiComponentsPages/feedback/NotificationsPage'));
const GoogleMaps = React.lazy(() => import('@app/pages/maps/GoogleMapsPage/GoogleMapsPage'));
const LeafletMaps = React.lazy(() => import('@app/pages/maps/LeafletMapsPage/LeafletMapsPage'));
const ReactSimpleMaps = React.lazy(() => import('@app/pages/maps/ReactSimpleMapsPage/ReactSimpleMapsPage'));
const PigeonsMaps = React.lazy(() => import('@app/pages/maps/PigeonsMapsPage/PigeonsMapsPage'));
const Logout = React.lazy(() => import('./Logout'));

export const NFT_DASHBOARD_PATH = '/admin/';
export const MEDICAL_DASHBOARD_PATH = '/medical-dashboard';


const NftDashboard = withLoading(NftDashboardPage);
const NewsFeed = withLoading(NewsFeedPage);
const AdvancedForm = withLoading(AdvancedFormsPage);

// UI Components
const Buttons = withLoading(ButtonsPage);
const Spinners = withLoading(SpinnersPage);
const Inputs = withLoading(InputsPage);
const Checkboxes = withLoading(CheckboxesPage);
const Radios = withLoading(RadiosPage);
const Selects = withLoading(SelectsPage);
const Switches = withLoading(SwitchesPage);
const Uploads = withLoading(UploadsPage);
const Rates = withLoading(RatesPage);
const AutoCompletes = withLoading(AutoCompletesPage);
const Steps = withLoading(StepsPage);
const DateTimePickers = withLoading(DateTimePickersPage);
const Dropdowns = withLoading(DropdownsPage);
const Breadcrumbs = withLoading(BreadcrumbsPage);
const Tabs = withLoading(TabsPage);
const Avatars = withLoading(AvatarsPage);
const Badges = withLoading(BadgesPage);
const Collapse = withLoading(CollapsePage);
const Pagination = withLoading(PaginationPage);
const Modals = withLoading(ModalsPage);
const Popovers = withLoading(PopoversPage);
const Popconfirms = withLoading(PopconfirmsPage);
const Progress = withLoading(ProgressPage);
const Results = withLoading(ResultsPage);
const Alerts = withLoading(AlertsPage);
const NotificationsUI = withLoading(NotificationsUIPage);
const Skeletons = withLoading(SkeletonsPage);

const DataTables = withLoading(DataTablesPage);
const Charts = withLoading(ChartsPage);

// Maps
const Google = withLoading(GoogleMaps);
const Leaflet = withLoading(LeafletMaps);
const ReactSimple = withLoading(ReactSimpleMaps);
const Pigeons = withLoading(PigeonsMaps);

const ServerError = withLoading(ServerErrorPage);
const Error404 = withLoading(Error404Page);

// Profile
const PersonalInfo = withLoading(PersonalInfoPage);
const SecuritySettings = withLoading(SecuritySettingsPage);
const Notifications = withLoading(NotificationsPage);
const Payments = withLoading(PaymentsPage);

const AuthLayoutFallback = withLoading(AuthLayout);
const MiscellaneousSettings = withLoading(MiscellaneousSettingsPage);
const LogoutFallback = withLoading(Logout);
let user = readUser();
export const AppRouter: React.FC = () => {
  const protectedLayout = (
    <RequireAuth>
      <MainLayout />
    </RequireAuth>
  );

  return (
    <BrowserRouter>
      <Routes>
        {/*START OF PROTECTED ROUTES           */}

        {/* {user?.user_type == 1 && */}
        <Route path={NFT_DASHBOARD_PATH} element={protectedLayout}>
          <Route index element={<NftDashboard />} />
          {/* <Route path={MEDICAL_DASHBOARD_PATH} element={<MedicalDashboard />} /> */}



          {/* <Route path="apps">
              <Route path="feed" element={<NewsFeed />} />
            </Route>
            <Route path="users">
              <Route path="list" element={<ManageUsers />} />
              <Route path="add" element={<AddUser />} />
              <Route path="edit/:id" element={<EditUser />} />
            </Route> */}

          <Route path="categories">
            <Route path="list" element={<CategoriesPage />} />
            <Route path="add" element={<AddCategory />} />
            <Route path="edit/:id" element={<EditCategory />} />
          </Route>

          <Route path="push-notifications">
            <Route path="list" element={<PushNotificationsList />} />
            <Route path="add" element={<AddPushNotification />} />
            <Route path="edit/:id" element={<EditPushNotification />} />
          </Route>

          <Route path="variants">
            <Route path="list" element={<VariantPage />} />
            <Route path="add" element={<AddVariant />} />
            <Route path="edit/:id" element={<EditVariant />} />


          </Route>

          <Route path="manage-areas">
            <Route path="view" element={<ManageAreasPage />} />

          </Route>
          <Route path="products">
            <Route path="list" element={<ProductsPage />} />
            <Route path="add" element={<AddProduct />} />
            <Route path="edit/:id" element={<EditProduct />} />


          </Route>
          <Route path="users">
            <Route path="list" element={<UsersPage />} />
            <Route path="add" element={<AddUser />} />
            <Route path="edit/:id" element={<EditUser />} />


          </Route>

          <Route path="orders">
            <Route path="list" element={<ManageOrder />} />

            <Route path="edit/:id" element={<EditOrder />} />
          </Route>
          <Route path="invoices">
            <Route path="list" element={<ManageInvoices />} />


          </Route>

          <Route path="subscriptions">
            <Route path="list" element={<ManageSubscription />} />

            <Route path="deliveries/:id" element={<EditSubscription />} />
          </Route>

          <Route path="deliveries">
            <Route path="list" element={<ManageDeliveries />} />

            <Route path="edit/:id" element={<EditDelivery />} />
          </Route>
          <Route path="riders">
            <Route path="list" element={<ManageRider />} />

            <Route path="edit/:id" element={<EditRider />} />
          </Route>
          <Route path="transactions">
            <Route path="list" element={<ManageTransactions />} />

            <Route path="edit/:id" element={<EditTransactions />} />
          </Route>

          <Route path="payment-logs">
            <Route path="list" element={<ManagePaymentLogs />} />
          </Route>


          <Route path="clients">
            <Route path="list" element={<ManageClient />} />

            <Route path="list/addresses/:id" element={<BasicTableClientAddresses />} />
          </Route>

          <Route path="recharge">
            <Route path="manage" element={<ManageRecharges />} />

          </Route>

          <Route path="refund">
            <Route path="manage" element={<ManageRefund />} />

          </Route>

          <Route path="delivery-slots">
            <Route path="list" element={<ManageDeliverySlots />} />
          </Route>

          <Route path="coupons">
            <Route path="manage" element={<ManageFirstRechargeCoupons />} />
          </Route>

          <Route path="meta-ads">
            <Route path="settings" element={<ManageMetaAds />} />
          </Route>

          <Route path="sliders" element={<ManageSliders />} />

          <Route path="misc-settings" element={<MiscellaneousSettings />} />
          
          {/* <Route path="medicines">
              <Route path="list" element={<ManageMedicine />} />
              <Route path="add" element={<AddMedicine />} />
              <Route path="edit/:id" element={<EditMedicine />} />
            </Route>

            <Route path="labtests">
              <Route path="list" element={<LabTestsList />} />
              <Route path="add" element={<AddLabTest />} />
              <Route path="edit/:id" element={<EditLabTest />} />
            </Route> */}




          <Route path="forms">
            <Route path="advanced-forms" element={<AdvancedForm />} />
          </Route>
          <Route path="data-tables" element={<DataTables />} />
          <Route path="charts" element={<Charts />} />
          <Route path="maps">
            <Route path="google-maps" element={<Google />} />
            <Route path="leaflet-maps" element={<Leaflet />} />
            <Route path="react-simple-maps" element={<ReactSimple />} />
            <Route path="pigeon-maps" element={<Pigeons />} />
          </Route>
          <Route path="server-error" element={<ServerError />} />
          <Route path="404" element={<Error404 />} />
          <Route path="profile" element={<ProfileLayout />}>
            <Route path="personal-info" element={<PersonalInfo />} />
            <Route path="security-settings" element={<SecuritySettings />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="payments" element={<Payments />} />


          </Route>
          <Route path="ui-components">
            <Route path="button" element={<Buttons />} />
            <Route path="spinner" element={<Spinners />} />
            <Route path="input" element={<Inputs />} />
            <Route path="checkbox" element={<Checkboxes />} />
            <Route path="radio" element={<Radios />} />
            <Route path="select" element={<Selects />} />
            <Route path="switch" element={<Switches />} />
            <Route path="upload" element={<Uploads />} />
            <Route path="rate" element={<Rates />} />
            <Route path="auto-complete" element={<AutoCompletes />} />
            <Route path="steps" element={<Steps />} />
            <Route path="date-time-picker" element={<DateTimePickers />} />
            <Route path="dropdown" element={<Dropdowns />} />
            <Route path="breadcrumbs" element={<Breadcrumbs />} />
            <Route path="tabs" element={<Tabs />} />
            <Route path="avatar" element={<Avatars />} />
            <Route path="badge" element={<Badges />} />
            <Route path="collapse" element={<Collapse />} />
            <Route path="pagination" element={<Pagination />} />
            <Route path="modal" element={<Modals />} />
            <Route path="popover" element={<Popovers />} />
            <Route path="popconfirm" element={<Popconfirms />} />
            <Route path="progress" element={<Progress />} />
            <Route path="result" element={<Results />} />
            <Route path="alert" element={<Alerts />} />
            <Route path="notification" element={<NotificationsUI />} />
            <Route path="skeleton" element={<Skeletons />} />
          </Route>
        </Route>
        {/* } */}
        {/*END OF PROTECTED ROUTES           */}
        <Route path="/auth" element={<AuthLayoutFallback />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="sign-up" element={<SignUpPage />} />

          <Route
            path="lock"
            element={
              <RequireAuth>
                <LockPage />
              </RequireAuth>
            }
          />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="security-code" element={<SecurityCodePage />} />
          <Route path="otp-code" element={<OTPCodePage />} />
          <Route path="new-password" element={<NewPasswordPage />} />
        </Route>

        <Route path="thank-you" element={<ThankYouPage />} />
        <Route path="/logout" element={<LogoutFallback />} />

        <Route path="/" element={<Navigate to="/auth/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
};
